import React, { useEffect, useRef, useState } from "react"
import "./InstitutionListTable.scss"
import { useNavigate } from "react-router-dom"
import InstituteApi from "../../../Api/InstituteApi"
import {
  Button,
  Col,
  Row,
  Spinner,
  Table,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import { StandardPagination, StatusBadge, ActionIcon } from "components/Common"

const InstitutionDataTable = () => {
  document.title = "Institution"
  const navigate = useNavigate("")
  const op = useRef(null)
  const os = useRef(null)
  const accessToken = localStorage.getItem("accessToken")
  const [instituteDetails, setInstituteDetails] = useState([])
  const userData = JSON.parse(localStorage.getItem("userData"))
  const [filteredInstitutionDetails, setFilteredInstitutionDetails] = useState(
    [],
  )

  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterCategory, setFilterCategory] = useState("all")
  const [selectedInstitution, setSelectedInstitution] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Institution", link: "/institutions" },
    { label: "Institution List" },
  ]
  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }
  const toast = useRef(null)
  const categoryOptions = [
    { name: "Medical", code: "Medical" },
    { name: "Engineering", code: "Engineering" },
    { name: "Arts", code: "Arts" },
    { name: "Science", code: "Science" },
    { name: "Commerce", code: "Commerce" },
    { name: "Law", code: "Law" },
    { name: "Management", code: "Management" },
    { name: "Architecture", code: "Architecture" },
    { name: "Agriculture", code: "Agriculture" },
    { name: "pharmacy", code: "pharmacy" },
  ]

  const [loading, setLoading] = useState(true)
  const [addInstitutionLoader, setAddInstitutionLoader] = useState(false)

  const handleViewInstitution = institution => {
    setSelectedInstitution(institution)
    setIsModalOpen(true)
  }

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
    if (!isModalOpen) setSelectedInstitution(null)
  }

  const getInstitution = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    InstituteApi.institutionList(config)
      .then(response => {
        setLoading(false)
        setInstituteDetails(response.data.institution)
        setTotalRecords(response.data.totalRecords)
      })
      .catch(error => {
        console.error("Error fetching institutions:", error)
      })
  }

  useEffect(() => {
    getInstitution()
  }, [accessToken, page, limit])

  useEffect(() => {
    let filteredData = instituteDetails

    if (filterStatus !== "all") {
      filteredData = filteredData.filter(
        institution => institution.verifiedStatus === filterStatus,
      )
    }

    if (filterCategory !== "all") {
      filteredData = filteredData.filter(
        institution => institution.category === filterCategory,
      )
    }

    setFilteredInstitutionDetails(filteredData)
  }, [filterStatus, filterCategory, instituteDetails])

  const totalPages = Math.ceil(totalRecords / limit)

  const uploadbtn = useRef()

  const handleUploadButtonClick = () => {
    uploadbtn.current.click()
  }

  const [excelLoader, setExcelLoader] = useState(false)

  const handleFileChange = event => {
    setExcelLoader(true)
    const file = event.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append("file", file)

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    InstituteApi.uploadInstitutionByExcel(formData, config)
      .then(res => {
        getInstitution()
        console.log(res.data)
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({ severity: "success", summary: "Success", detail: res.data.message })
      })
      .catch(err => {
        console.log(err)
        event.target.value = ""
        setExcelLoader(false)
        toast.current.show({ severity: "error", summary: "Error", detail: err.response.data.message })
      })
  }

  const handleAddInstitutionClick = () => {
    setAddInstitutionLoader(true)
    setTimeout(() => {
      setAddInstitutionLoader(false)
      navigate("/addInstituion")
    }, 500)
  }

  const [selectedInstitutions, setSelectedInstitutions] = useState([])

  const toggleSelectAll = () => {
    if (selectedInstitutions.length === filteredInstitutionDetails.length) {
      setSelectedInstitutions([])
    } else {
      setSelectedInstitutions(filteredInstitutionDetails.map(inst => inst._id))
    }
  }

  const toggleSelection = instId => {
    console.log(instId)
    setSelectedInstitutions(prevInst =>
      prevInst.includes(instId)
        ? prevInst.filter(id => id !== instId)
        : [...prevInst, instId],
    )
  }

  const handleUpdateStatus = newStatus => {
    if (selectedInstitutions.length === 0) {
      toast.current.show({ severity: "warn", summary: "Warning", detail: "Please select at least one Institution to update." })
      return
    }

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    const payload = {
      institutionIds: selectedInstitutions,
      newStatus: newStatus,
    }

    InstituteApi.updateInstitutionStatus(payload, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({ severity: "success", summary: "Success", detail: response.data.message })
          getInstitution()
          setSelectedInstitutions([])
        }
      })
      .catch(error => {
        console.error("Error updating Institution status:", error)
        toast.current. show({ severity: "error", summary: "Error", detail: "Failed to update Institution status." })
      })
  }

  const uniqueCategories = [
    ...new Set(instituteDetails.map(inst => inst.category)),
  ]

  return (
    <>
      <Toast ref={toast} />
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>Institution Details</ModalHeader>
        <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px' }}>
          {selectedInstitution && (
            <Card>
              <CardBody>
                <div className="institution-details">
                  <h5 className="mb-4">Basic Information</h5>
                  <Row className="mb-3">
                    <Col md={6}>
                      <p>
                        <strong>Institution Name:</strong>{" "}
                        {selectedInstitution.institutionName}
                      </p>
                      <p>
                        <strong>Institution Code:</strong>{" "}
                        {selectedInstitution.institutionCode}
                      </p>
                      <p>
                        <strong>Type:</strong> {selectedInstitution.type}
                      </p>
                      <p>
                        <strong>Category:</strong>{" "}
                        {selectedInstitution.category}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p>
                        <strong>Year Established:</strong>{" "}
                        {selectedInstitution.yearEstablished}
                      </p>
                      <p>
                        <strong>UGC Approval:</strong>{" "}
                        {selectedInstitution.ugcApproval ? "Yes" : "No"}
                      </p>
                      <p>
                        <strong>Verification Status:</strong>
                        <StatusBadge status={selectedInstitution.verifiedStatus} />
                      </p>
                    </Col>
                  </Row>

                  <h5 className="mb-4">Contact Information</h5>
                  <Row className="mb-3">
                    <Col md={6}>
                      <p>
                        <strong>Email:</strong>{" "}
                        {selectedInstitution.email || "-"}
                      </p>
                      <p>
                        <strong>Phone:</strong>{" "}
                        {selectedInstitution.phone || "-"}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p>
                        <strong>Website:</strong>{" "}
                        {selectedInstitution.website || "-"}
                      </p>
                    </Col>
                  </Row>

                  <h5 className="mb-4">Address Details</h5>
                  <Row>
                    <Col md={12}>
                      <p>
                        <strong>Address:</strong>{" "}
                        {selectedInstitution.address || "-"}
                      </p>
                      <p>
                        <strong>City:</strong> {selectedInstitution.city || "-"}
                      </p>
                      <p>
                        <strong>State:</strong>{" "}
                        {selectedInstitution.state || "-"}
                      </p>
                      <p>
                        <strong>Country:</strong>{" "}
                        {selectedInstitution.country || "-"}
                      </p>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          )}
        </ModalBody>
      </Modal>
      <Row>
        <Col xs="12">
          <div className="institution-list-header">
            <div className="header-content d-flex align-items-start justify-content-between">
              <div className="left-section">
                <CustomBreadCrumb data={breadcrumbItems} pageTitle="Institution" handleClick={handleBreadcrumbClick} />
                
                {selectedInstitutions.length > 0 && (
                  <div className="action-buttons d-flex align-items-center gap-3 mt-2">
                    <Button
                      type="button"
                      color="primary"
                      className="waves-effect"
                      onClick={() => handleUpdateStatus("approve")}
                    >
                      Approve
                    </Button>
                    <Button
                      type="button"
                      color="danger"
                      className="waves-effect"
                      onClick={() => handleUpdateStatus("reject")}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="data-table-container">
            <div className="table-responsive mt-4 mb-3">
              {loading ? (
                <DataTableLoader cols={10} />
              ) : (
                <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onClick={toggleSelectAll}
                        checked={
                          selectedInstitutions.length ===
                          filteredInstitutionDetails.length
                        }
                      />
                    </th>
                    <th>#</th>
                    <th>Institution Name</th>
                    <th>Type</th>
                    <th>Year Established</th>
                    <th>
                      <span className="d-flex align-items-center gap-2 justify-content-center">
                        <span>Category</span>
                        <span
                          onClick={e => op.current.toggle(e)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="mdi mdi-filter" />
                        </span>
                        <OverlayPanel ref={op}>
                          <Input
                            type="select"
                            value={filterCategory}
                            onChange={e => {
                              setFilterCategory(e.target.value)
                              op.current.hide()
                            }}
                            style={{ width: "150px" }}
                          >
                            <option value="all">All</option>
                            {categoryOptions.map((category, index) => (
                              <option key={index} value={category.code}>
                                {category.name}
                              </option>
                            ))}
                          </Input>
                        </OverlayPanel>
                      </span>
                    </th>
                    <th>UGC Approval</th>
                    <th>
                      <span className="d-flex align-items-center gap-0 justify-content-center">
                        <span>Verified Status</span>
                        <span
                          onClick={e => os.current.toggle(e)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="mdi mdi-filter" />
                        </span>
                      </span>
                      <OverlayPanel ref={os} className="filter-overlay ">
                        <Input
                          type="select"
                          value={filterStatus}
                          onChange={e => {
                            setFilterStatus(e.target.value)
                            os.current.hide()
                          }}
                          style={{ width: "150px" }}
                          className="filter-select"

                        >
                          <option value="all">All</option>
                          <option value="approve">Approved</option>
                          <option value="reject">Rejected</option>
                          <option value="pending">Pending</option>
                        </Input>
                      </OverlayPanel>
                    </th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstitutionDetails.map((institution, index) => (
                    <tr key={institution._id} className="university-table-row">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedInstitutions.includes(
                            institution._id,
                          )}
                          onClick={() => {
                            toggleSelection(institution._id)
                          }}
                        />
                      </td>
                      <th scope="row">{(page - 1) * limit + index + 1}</th>
                      <td>{institution.institutionName}</td>
                      <td>{institution.type}</td>
                      <td>{institution.yearEstablished}</td>
                      <td>{institution.category}</td>

                      <td>{institution.ugcApproval ? "Yes" : "No"} </td>
                      <td>
                        <StatusBadge status={institution.verifiedStatus} />
                      </td>

                      <td>
                        <ActionIcon
                          type="view"
                          onClick={() => handleViewInstitution(institution)}
                          tooltip="View Institution"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                </Table>
              )}
            </div>
          </div>
          <StandardPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Col>
      </Row>
    </>
  )
}

export default InstitutionDataTable
